import React, { useState, useEffect, useRef } from 'react';
import { usePdfJob } from 'context/PdfJobContext';
import {
  LinearProgress,
  IconButton,
  Paper,
  Typography,
  Box,
  Collapse,
  Tooltip,
  Button,
} from '@material-ui/core';
import {
  Close as CloseIcon,
  GetApp as DownloadIcon,
  Error as ErrorIcon,
  CheckCircle as SuccessIcon,
  ExpandMore,
  ExpandLess,
  ClearAll,
} from '@material-ui/icons';

const canDownloadJob = (job) => job?.status === 'COMPLETED' && typeof job.downloadFn === 'function';

const canRetryJob = (job) => job?.status === 'FAILED' && typeof job.run === 'function';

const getFriendlyError = (error) => {
  if (!error) return 'PDF could not be generated. Please try again.';
  const msg = String(error).toLowerCase();
  if (msg.includes('cancel')) return 'PDF generation was cancelled.';
  if (msg.includes('timeout') || msg.includes('timed out'))
    return 'This is taking longer than expected. You can try again later.';
  if (
    msg.includes('not found') ||
    msg.includes('job id') ||
    msg.includes('job_id') ||
    msg.includes('404') ||
    msg.includes('service may have restarted') ||
    msg.includes('no pdf found')
  )
    return 'PDF is not ready yet. Please generate it again.';
  if (msg.includes('network') || msg.includes('econnrefused') || msg.includes('fetch'))
    return 'Connection issue. Please check your connection and try again.';
  if (msg.includes('storage') || msg.includes('s3') || msg.includes('upload'))
    return 'PDF could not be saved. Please try again.';
  if (msg.includes('failed') || msg.includes('error'))
    return 'PDF could not be generated. Please try again.';
  return 'Something went wrong. Please try again.';
};

const JobItem = ({ job, onRemove, onDownload, onRetry }) => {
  const canDownload = canDownloadJob(job);
  const canRetry = canRetryJob(job);

  const getStatusColor = () => {
    switch (job.status) {
      case 'COMPLETED':
        return '#4caf50';
      case 'FAILED':
        return '#f44336';
      case 'CANCELLED':
        return '#757575';
      case 'PROCESSING':
        return '#7e1f8c';
      case 'SENDING':
        return '#ff9800';
      case 'FETCHING':
        return '#2196f3';
      case 'QUEUED':
        return '#9e9e9e';
      default:
        return '#757575';
    }
  };

  const getStatusLabel = () => {
    switch (job.status) {
      case 'QUEUED':
        return 'Queued';
      case 'RECOVERING':
        return 'Resuming';
      case 'FETCHING':
      case 'SENDING':
        return 'Preparing';
      case 'PROCESSING':
        return job.progress >= 90 ? 'Almost Ready' : 'Generating';
      case 'COMPLETED':
        return 'Ready';
      case 'FAILED':
        return 'Failed';
      case 'CANCELLED':
        return 'Cancelled';
      default:
        return 'Working';
    }
  };

  const getStatusDescription = () => {
    switch (job.status) {
      case 'QUEUED':
        return 'Waiting to start. This will begin shortly.';
      case 'RECOVERING':
        return 'Checking on your PDF. This will continue shortly.';
      case 'FETCHING':
        return 'Fetching content for your PDF.';
      case 'SENDING':
        return 'Starting PDF generation. This may take a few minutes.';
      case 'PROCESSING':
        if (job.progress >= 92) return 'Almost done! Finishing up your PDF.';
        if (job.progress >= 75) return 'Nearly there. Wrapping up the PDF.';
        if (job.progress >= 40) return 'Generating your PDF. Please wait.';
        return 'Processing your content. This may take a few minutes for large documents.';
      case 'COMPLETED':
        return job.downloaded ? 'PDF downloaded successfully.' : 'Your PDF is ready to download.';
      case 'FAILED':
        return getFriendlyError(job.error);
      case 'CANCELLED':
        return 'PDF generation was cancelled.';
      default:
        return '';
    }
  };

  return (
    <Box mb={2} p={1} style={{ borderBottom: '1px solid #eee' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography
          variant="caption"
          style={{
            fontWeight: 'bold',
            color: '#555',
            maxWidth: '75%',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {job.title}
        </Typography>
        <Box display="flex" alignItems="center">
          {job.status === 'COMPLETED' && (
            <Box display="flex" alignItems="center">
              <Tooltip title={job.downloaded ? 'Ready (Downloaded)' : 'Ready'}>
                <SuccessIcon style={{ color: '#4caf50', fontSize: 18 }} />
              </Tooltip>
            </Box>
          )}
          {job.status === 'FAILED' && (
            <Tooltip title="Could not generate PDF">
              <ErrorIcon style={{ color: '#f44336', fontSize: 18 }} />
            </Tooltip>
          )}
          {job.status === 'CANCELLED' && (
            <Tooltip title="Cancelled">
              <CloseIcon style={{ color: '#9e9e9e', fontSize: 18 }} />
            </Tooltip>
          )}
          {job.status === 'QUEUED' && (
            <Typography
              variant="caption"
              style={{ color: '#9e9e9e', fontSize: 10, marginRight: 5 }}
            >
              QUEUED
            </Typography>
          )}
          {['PROCESSING', 'FETCHING', 'SENDING', 'RECOVERING'].includes(job.status) && (
            <Box
              className="spinner-border spinner-border-sm"
              role="status"
              style={{ width: 14, height: 14, color: getStatusColor() }}
            />
          )}
          <Tooltip title="Clear Job">
            <IconButton size="small" onClick={() => onRemove(job.id)} style={{ marginLeft: 8 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <Box display="flex" alignItems="center">
        <Box width="100%" mr={1}>
          <LinearProgress
            variant={
              job.status === 'QUEUED' || job.status === 'RECOVERING'
                ? 'indeterminate'
                : 'determinate'
            }
            value={
              job.status === 'QUEUED' || job.status === 'RECOVERING' ? undefined : job.progress
            }
            style={{
              height: 6,
              borderRadius: 3,
              backgroundColor: '#e0e0e0',
            }}
            classes={{ barColorPrimary: { backgroundColor: getStatusColor() } }}
          />
        </Box>
        <Box minWidth={35}>
          <Typography variant="body2" color="textSecondary">
            {job.status === 'QUEUED' || job.status === 'RECOVERING'
              ? '-'
              : `${Math.round(job.progress)}%`}
          </Typography>
        </Box>
      </Box>
      <Typography
        variant="caption"
        style={{
          color: getStatusColor(),
          fontSize: 10,
          textTransform: 'uppercase',
          fontWeight: 'bold',
        }}
      >
        {getStatusLabel()}
      </Typography>
      <Typography
        variant="caption"
        style={{
          color: '#777',
          fontSize: 10,
          display: 'block',
          marginTop: 4,
        }}
      >
        {getStatusDescription()}
      </Typography>
      {canRetry && (
        <Box mt={1}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => onRetry(job.id)}
            style={{
              borderColor: '#ff9800',
              color: '#e65100',
              fontSize: 11,
              fontWeight: 600,
              textTransform: 'none',
              padding: '3px 10px',
              marginRight: 6,
            }}
          >
            Retry
          </Button>
        </Box>
      )}
      {canDownload && (
        <Box mt={1}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => onDownload(job)}
            style={{
              borderColor: '#4caf50',
              color: '#2e7d32',
              fontSize: 11,
              fontWeight: 600,
              textTransform: 'none',
              padding: '3px 10px',
            }}
          >
            <DownloadIcon fontSize="small" style={{ marginRight: 6 }} />
            {job.downloaded ? 'Download Again' : 'Download PDF'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

const JobTracker = () => {
  const { jobs, clearJobs, removeJob, retryJob, downloadTrackedJobPdf } = usePdfJob();
  const [expanded, setExpanded] = useState(true);
  const [isVisible, setIsVisible] = useState(true);
  const prevJobsRef = useRef(jobs);

  useEffect(() => {
    if (jobs.length > 0 && jobs !== prevJobsRef.current) {
      const hasNewOrCompleted = jobs.some((job) => {
        const prevJob = prevJobsRef.current.find((j) => j.id === job.id);
        if (!prevJob) return true; // Reappear for new job
        return (
          (job.status === 'COMPLETED' || job.status === 'FAILED') && prevJob.status !== job.status
        );
      });
      if (hasNewOrCompleted) {
        setIsVisible(true);
        if (!expanded) setExpanded(true);
      }
    }
    prevJobsRef.current = jobs;
  }, [jobs, expanded]);

  if (jobs.length === 0 || !isVisible) return null;

  return (
    <Paper
      elevation={6}
      style={{
        position: 'fixed',
        bottom: 20,
        right: 20,
        width: 320,
        zIndex: 1400,
        borderRadius: 8,
        overflow: 'hidden',
        border: '1px solid #7e1f8c',
      }}
    >
      <Box
        bgcolor="#7e1f8c"
        color="white"
        p={1.5}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        onClick={() => setExpanded(!expanded)}
        style={{ cursor: 'pointer' }}
      >
        <Box display="flex" alignItems="center">
          <DownloadIcon size="small" style={{ marginRight: 8 }} />
          <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
            PDF Export Jobs ({jobs.length})
          </Typography>
        </Box>
        <Box display="flex" alignItems="center">
          <Tooltip title="Clear Finished Jobs">
            <IconButton
              size="small"
              color="inherit"
              onClick={(e) => {
                e.stopPropagation();
                clearJobs();
              }}
            >
              <ClearAll fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={expanded ? 'Minimize' : 'Expand'}>
            <IconButton size="small" color="inherit">
              {expanded ? <ExpandMore /> : <ExpandLess />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Close Box">
            <IconButton
              size="small"
              color="inherit"
              onClick={(e) => {
                e.stopPropagation();
                setIsVisible(false);
              }}
              style={{ marginLeft: 4 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Collapse in={expanded}>
        <Box p={2} style={{ maxHeight: 400, overflowY: 'auto', backgroundColor: 'white' }}>
          {jobs.map((job) => (
            <Box key={job.id} p={0.5}>
              <JobItem
                job={job}
                onRemove={removeJob}
                onDownload={downloadTrackedJobPdf}
                onRetry={retryJob}
              />
            </Box>
          ))}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default JobTracker;
